import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import TicketDelegate from './ticket.delegate';
import MultimediaController from './multimedia.controller';
import IssueStatusMapController from '../common/issueTracker/issueStatusMap.controller';
import IssueDetailController from '../common/issueTracker/issueDetail.controller';
import { SupportTicketConstants } from './support.enums';

class TicketController extends Inherit(
    ClockoutController,
    MultimediaController,
    IssueStatusMapController,
    IssueDetailController,
    TicketDelegate,
) {
    state = {
        user: {},
        ticket: null,
        ticketLoading: true,
        replyBody: '',
        replyAttachments: [],
        replySending: false,
        messagesLoadingMore: false,
        // Served status vocabulary for the linked-issue card (KAN-628).
        issueStatusMap: null,

        // The linked issue's detail, opened from that card. Same modal the Jira
        // tab uses, so an issue reads identically from either side of the link.
        issueDetailKey: null,
        issueDetail: null,
        issueDetailLoading: false,
        issueDetailError: false
    };

    async initialize() {
        if (!this.user?.access?.length) {
            this.navigateLogin();
            return;
        }

        const ticketId = this.getTicketIdFromRoute()
        this.setTicketInitialState(ticketId);

        // Fire-and-forget: the linked-issue pill renders with its bundled label
        // until this lands, and the data layer caches it for the session.
        this.loadIssueStatusMap();

        await this.loadTicket();
    }

    /**
     * Teardown. The linked-issue card fetches the served status map and the
     * issue detail, and both guard their `setState` on `_unmounted` — a flag
     * nothing on this screen set until now, which made those guards dead and let
     * a slow response land after the ticket page had gone.
     */
    async cleanup() {
        this._unmounted = true;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.params?.ticketId !== this.props.params?.ticketId) {
            // This route renders the SAME component for every ticket, so a
            // navigation only changes the param — nothing unmounts and no state
            // resets on its own. The issue modal belongs to the ticket that was
            // on screen when it was opened, so it has to be closed explicitly:
            // otherwise clicking a linked support ticket inside it lands on that
            // ticket with the previous ticket's modal still sitting open.
            //
            // Covers the browser's back and forward buttons too, not just the
            // in-modal link.
            this.closeIssueDetail();
            this.loadTicket();
        }
    }

    getTicketIdFromRoute() {
        return this.props.params?.ticketId ?? null;
    }

    async loadTicket() {
        const ticketId = this.getTicketIdFromRoute();
        if (!ticketId) {
           this.setInvalidTicketState();
            return;
        }

        this.setTicketLoading(true);
        const detail = await this.data.fetchTicket(ticketId);
        this.setTicketDetail(this.normalizeTicketDetailPayload(detail));
        this.setTicketLoading(false);

        if(!detail) return;

        try {
            await this.data.markTicketRead(ticketId);
        } catch (e) {
            /* non-blocking */
        }
        this.scrollToHash();
    }

    mergeTicketMessages(existing, incoming) {
        const existingArray = Array.isArray(existing) ? existing : [];
        const incomingArray = Array.isArray(incoming) ? incoming : [];
        return [...existingArray, ...incomingArray];
    }

    async loadMoreTicketMessages() {
        const ticketId = this.getTicketIdFromRoute();
        const ticket = this.state.ticket;
        const pi = ticket?.messagesPageInfo;
        if (!ticketId || !ticket || this.state.messagesLoadingMore || !pi?.hasNext) return;
        if (this._ticketMessageFetchInFlight) return;
        this._ticketMessageFetchInFlight = true;

        const nextPage = (pi.page || 1) + 1;
        const prevDocHeight = document.documentElement.scrollHeight;
        const prevScrollY = window.scrollY;
        const firstMsgIdBefore = ticket.messages?.[0]?.id;

        this.setMessagesloadingMore(true);
        try {
            const raw = await this.data.fetchTicket(ticketId, {
                page: nextPage,
                perPage: SupportTicketConstants.DETAIL_MESSAGES_PER_PAGE
            });
            const chunk = this.normalizeTicketDetailPayload(raw);
            if (!chunk) {
                this.setMessagesloadingMore(false);
                return;
            }

            const merged = this.mergeTicketMessages(ticket.messages, chunk.messages);
            const prependedOlder = merged?.[0]?.id !== firstMsgIdBefore;
            const newTicketdetail = {
                ...ticket,
                messages: merged,
                messagesPageInfo: chunk.messagesPageInfo
            };
            this.setTicketDetail(newTicketdetail, () => {
                if (!prependedOlder) return;
                requestAnimationFrame(() => {
                    const delta = document.documentElement.scrollHeight - prevDocHeight;
                    if (delta > 0) window.scrollTo(window.scrollX, prevScrollY + delta);
                });
            });
        } catch (e) {
            this.setMessagesloadingMore(false);
        } finally {
            this._ticketMessageFetchInFlight = false;
        }
    }

    scrollToHash() {
        const hash = window.location.hash;
        if (!hash || hash.length < 2) return;
        const id = decodeURIComponent(hash.slice(1));
        requestAnimationFrame(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    async handleSendReply() {
        const ticketId = this.getTicketIdFromRoute();
        const { replyBody, replyAttachments } = this.state;
        const body = (replyBody || '').trim();
        if (!ticketId || (!body && replyAttachments.length === 0)) return;

        const status = this.state.ticket?.status;
        if (status === SupportTicketConstants.STATUS.RESOLVED || status === SupportTicketConstants.STATUS.CLOSED) {
            return;
        }

        this.setState({ replySending: true });
        try {
            // Validate and upload attachments before replying
            let files = [];
            if (replyAttachments.length > 0) {
                files = await this.uploadAttachments(replyAttachments, {
                    requestPresigned: this.data.requestPresignedTicketUrl.bind(this.data),
                    uploadToS3: this.data.uploadFileToS3.bind(this.data)
                });
            }

            await this.data.replyToTicket(ticketId, body, files);
            this.props.notificationApi.success({ message: 'Reply sent' });
            for (const att of replyAttachments) {
                if (att.url?.startsWith('blob:')) URL.revokeObjectURL(att.url);
            }
            this.setReplyBody('');
            this.setReplyAttachments([]);
            await this.loadTicket();
        } catch (error) {
            // Distinguish between upload and reply creation failures
            if (error.code === 'UPLOAD_PARTIAL_FAILURE') {
                this.props.notificationApi.error({
                    message: 'File upload failed',
                    description: error.message + ' Please try again or contact support if files remain inaccessible.'
                });
            } else if (error.code === 'UPLOAD_FAILED') {
                this.props.notificationApi.error({
                    message: 'File upload failed',
                    description: error.message || 'Could not upload attachments. Please try again.'
                });
            } else {
                const desc = error?.response?.data?.error || error?.message || 'Could not send reply.';
                this.props.notificationApi.error({
                    message: 'Reply failed',
                    description: desc
                });
            }
        } finally {
            this.setReplySending(false);
        }
    }

    /**
     * Normalize GET ticket API shape `{ ticket: { ticket, messages } }` to a flat ticket + messages array.
     * @param {*} raw — `response.data` from GET ticket
     */
    normalizeTicketDetailPayload(raw) {
        const wrap = raw?.ticket;
        if (!wrap) return null;
        const inner = wrap.ticket;
        const msgBlock = wrap.messages;
        if (inner && msgBlock && Array.isArray(msgBlock.items)) {
            return {
                ...inner,
                messages: msgBlock.items,
                messagesPageInfo: msgBlock.pageinfo
            };
        }
        if (inner && Array.isArray(wrap.messages)) {
            return { ...inner, messages: wrap.messages, messagesPageInfo: null };
        }
        return null;
    }
}

export default TicketController;
